import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + '/js/pdf.worker.min.js';

// The DYMO Connect Framework client library is vendored locally (see
// public/js/dymo.connect.framework.js, MIT-licensed by DYMO/Sanford) - the
// local DYMO Connect service does NOT serve this script itself, it only
// exposes REST endpoints (GetPrinters, PrintLabel, etc.) at 127.0.0.1:41951
// which this library calls internally once loaded. If DYMO Connect isn't
// installed/running on this workstation, those internal calls fail and the
// SDK reports no printers, so callers fall back to the normal browser print
// flow.
const DYMO_FRAMEWORK_URL = process.env.PUBLIC_URL + '/js/dymo.connect.framework.js';
const DYMO_SCRIPT_LOAD_TIMEOUT_MS = 2000;

const LABEL_PRINTER_TYPE = 'LabelWriterPrinter';
const PREFERRED_PRINTER_NAME_HINT = '550';

const IMAGE_OBJECT_NAME = 'LabelImage';

const LOG_PREFIX = 'DYMO label print:';

// DYMO 30326 "Video Top" label, 1-4/5 in x 3-1/10 in (46mm x 79mm), printed
// landscape. PAPER_NAME has to match DYMO's own catalog exactly or printing
// fails with "Sch_UndeclaredElement"; this one is taken from the DieCutSKU
// entry for SKU 30326 embedded in DYMO.LabelAPI.dll and checked against the
// local DYMO Web Service, which renders the label 3.05 in x 1.80 in.
const PAPER_NAME = '30326 Video Top';
const LABEL_WIDTH_TWIPS = 4464; // 3.1 in
const LABEL_HEIGHT_TWIPS = 2592; // 1.8 in
const LABEL_MARGIN_TWIPS = 72; // 0.05 in
const RENDER_DPI = 300;
const TARGET_IMAGE_WIDTH_PX = Math.round((LABEL_WIDTH_TWIPS / 1440) * RENDER_DPI);
const TARGET_IMAGE_HEIGHT_PX = Math.round((LABEL_HEIGHT_TWIPS / 1440) * RENDER_DPI);

// How close to square an image has to be to be considered the QR code.
const SQUARE_TOLERANCE = 0.1;
// Caps the offscreen page canvas if a PDF ever places the QR very small.
const MAX_PAGE_RENDER_PX = 3000;
// The generated QR carries a ~5.6 module quiet zone, well over the 4 modules
// the spec asks for. Trimming it back to roughly 2 modules prints the code
// itself bigger; the label's padding and its unprinted border supply the rest.
const QUIET_ZONE_RATIO = 0.05;
// Render the QR larger than the label needs, so scaling down to the final size
// resamples downwards and keeps the module edges crisp.
const QR_OVERSAMPLE = 1.5;
// Below this red channel value a pixel counts as part of the printed code.
const DARK_THRESHOLD = 128;
// Anything visibly non-white counts when centring the finished label, so a
// lighter-coloured part of the logo still counts as content.
const INK_THRESHOLD = 230;

// Layout taken from the PAN PDF itself so the label reads like the letter:
// PAN, program name and account ID stacked above the code, a rule above and
// below it, and the TotilPay logo below the bottom rule. Every measurement is
// expressed relative to the code, which the PDF sets at 165pt, so the whole
// block scales to whatever label it prints on.
//
// The label's long edge (3.1in) is too short to lay the block out sideways as
// printed, so the block is composed portrait - the same proportions as the
// PDF - on a virtual canvas as tall as the label is wide, then that canvas is
// rotated 90deg into the label's actual landscape shape. That gives the block
// the label's long edge to grow along instead of its short one, which is what
// makes the code and text noticeably bigger than a landscape-composed block
// could manage; the label simply reads sideways as a result.
//
// The PDF sets the PAN rotated, running down the code's left edge - a design
// that depends on there being room to its side, which a 90deg-rotated layout
// doesn't have (the code already spans nearly the label's full width once
// rotated). So instead of trying to preserve that exact position, the PAN
// becomes a third stacked line above the code, alongside the program name and
// account ID, drawn upright like they are rather than pre-rotated - the whole
// block's own 90deg rotation is what turns it sideways in the end, the same
// way it turns the program name and account ID sideways.
const LABEL_PADDING_PX = 20;
const PDF_QR_PT = 165;
const PROGRAM_FONT_RATIO = 12 / PDF_QR_PT;
// 15% larger than the PDF's 10pt, growing upwards from its unchanged baseline
// into the gap below the program name, so nothing else moves.
const ACCOUNT_FONT_RATIO = 11.5 / PDF_QR_PT;
const PAN_FONT_RATIO = 11 / PDF_QR_PT;
// The block's long edge is full, so space is moved rather than added: the
// program name (and the PAN above it) sit TIGHTEN_PT closer to the account ID
// than the PDF has them, and the website drops by the same amount to open up
// the gap below the logo. The block's total length is unchanged.
const TIGHTEN_PT = 2.5;
const PROGRAM_BASELINE_RATIO = (43.4 - TIGHTEN_PT) / PDF_QR_PT; // above the top of the code
const ACCOUNT_BASELINE_RATIO = 20.33 / PDF_QR_PT;
// The PAN becomes the topmost line, one more row above the program name at
// roughly the same line spacing the program name keeps above the account ID.
const PAN_ROW_GAP_RATIO = PAN_FONT_RATIO * 1.9;
const PAN_BASELINE_RATIO = PROGRAM_BASELINE_RATIO + PAN_ROW_GAP_RATIO;
const RULE_ABOVE_RATIO = 4.5 / PDF_QR_PT;
const RULE_BELOW_RATIO = 5.5 / PDF_QR_PT;
const RULE_WIDTH_RATIO = 160 / PDF_QR_PT;
const RULE_THICKNESS_RATIO = 6 / PDF_QR_PT;
// The PDF's logo sits 14.5pt below the bottom rule, at 108.7x30pt.
const LOGO_GAP_RATIO = 14.5 / PDF_QR_PT;
const LOGO_WIDTH_RATIO = 108.7 / PDF_QR_PT;
const LOGO_HEIGHT_RATIO = 30 / PDF_QR_PT;
// The website goes under the logo, as on the PDF. It's drawn from this
// constant rather than read off the page, so it doesn't depend on picking
// which upright line of the PDF's text it is. Its bottom edge is pinned where
// it has always been, and the text grows upwards into the gap below the logo
// instead, stopping ~4px short of it.
const WEBSITE_TEXT = 'tims.totilpay.com';
const WEBSITE_FONT_RATIO = 12.5 / PDF_QR_PT;
const WEBSITE_BOTTOM_RATIO = (13.5 + TIGHTEN_PT) / PDF_QR_PT; // below the bottom of the logo
const WEBSITE_BASELINE_RATIO = WEBSITE_BOTTOM_RATIO - WEBSITE_FONT_RATIO * 0.25;
// The PDF's reminder below the website: left-aligned from the rules' left end
// and wrapped to their width, at the PDF's 10pt. There's no spare room left on
// the long edge for it, so fitInk() shrinks the whole block slightly to fit.
const MESSAGE_TEXT = 'Keep this card. It can be used to access current and future benefits.';
const MESSAGE_FONT_RATIO = 10 / PDF_QR_PT;
const MESSAGE_GAP_RATIO = 14 / PDF_QR_PT; // website baseline to the message's first baseline
const MESSAGE_LINE_RATIO = MESSAGE_FONT_RATIO * 1.15;
// How far the block reaches past the code, used to size it against the label.
const BLOCK_ABOVE_RATIO = PAN_BASELINE_RATIO + PAN_FONT_RATIO * 0.75;
const BLOCK_BELOW_RATIO = RULE_BELOW_RATIO + RULE_THICKNESS_RATIO + LOGO_GAP_RATIO + LOGO_HEIGHT_RATIO;
const WEBSITE_BELOW_RATIO = WEBSITE_BOTTOM_RATIO;
// Pre-rotation, the composition is portrait: as wide as the label is tall,
// and as tall as the label is wide.
const COMPOSED_WIDTH_PX = TARGET_IMAGE_HEIGHT_PX;
const COMPOSED_HEIGHT_PX = TARGET_IMAGE_WIDTH_PX;
// The website and message lines are deliberately left out of this sizing.
// These extents are estimates, and the logo's box in particular includes
// whatever white padding the logo image carries, so counting them too would
// shrink the code more than it needs. fitInk() checks the real ink instead,
// and only scales down as far as it genuinely has to.
const QR_SIZE_PX = Math.round(Math.min(
	(COMPOSED_HEIGHT_PX - LABEL_PADDING_PX * 2) / (1 + BLOCK_ABOVE_RATIO + BLOCK_BELOW_RATIO),
	COMPOSED_WIDTH_PX - LABEL_PADDING_PX * 2
));
// The smallest margin left around the ink once it's fitted to the label. The
// label XML's Bounds already hold the image 0.05in in from the label's edge.
const INK_MARGIN_PX = 4;
// Printed labels come out with a little more blank space on the left than
// the right, reading the label the way its text runs (across its short 1.8in
// edge). DYMO reports this stock's printable margins on that edge as 4.08pt
// and 2.88pt, and the printer adds some offset of its own. That edge has room
// to spare, so the content is simply moved left this far - no shrinking.
// 8px wasn't enough on a physical print. 16px is the most the content can
// move within the image: the 500px code leaves 20px either side of it, less
// the 4px INK_MARGIN_PX.
const SHORT_EDGE_SHIFT_PX = 16;
const MIN_FONT_PX = 12;
const LABEL_FONT = 'px Arial, Helvetica, sans-serif';

// Minimal valid 1x1 transparent PNG, replaced at print time via setObjectText.
const PLACEHOLDER_IMAGE_BASE64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR4nGNgAAIAAAUAAXpeqz8AAAAASUVORK5CYII=';

let frameworkLoadPromise = null;

function isDymoFrameworkPresent() {
	return !!(window.dymo && window.dymo.label && window.dymo.label.framework);
}

function loadDymoFramework() {
	if (frameworkLoadPromise)
		return frameworkLoadPromise;

	frameworkLoadPromise = new Promise((resolve) => {
		if (isDymoFrameworkPresent()) {
			resolve(true);
			return;
		}

		let settled = false;
		let finish = (result) => {
			if (settled) return;
			settled = true;
			resolve(result);
		};

		let script = document.createElement('script');
		script.src = DYMO_FRAMEWORK_URL;
		script.onload = () => finish(isDymoFrameworkPresent());
		script.onerror = () => finish(false);
		document.head.appendChild(script);

		setTimeout(() => finish(false), DYMO_SCRIPT_LOAD_TIMEOUT_MS);
	});

	return frameworkLoadPromise;
}

function findLabelWriterPrinterName() {
	let printers = window.dymo.label.framework.getPrinters() || [];
	let labelWriters = printers.filter((p) => p.printerType === LABEL_PRINTER_TYPE && p.isConnected);

	if (!labelWriters.length)
		return null;

	let preferred = labelWriters.find((p) => p.modelName && p.modelName.indexOf(PREFERRED_PRINTER_NAME_HINT) !== -1);
	return (preferred || labelWriters[0]).name;
}

function buildLabelXml() {
	let width = LABEL_WIDTH_TWIPS - (LABEL_MARGIN_TWIPS * 2);
	let height = LABEL_HEIGHT_TWIPS - (LABEL_MARGIN_TWIPS * 2);

	return '<?xml version="1.0" encoding="utf-8"?>' +
		'<DieCutLabel Version="8.0" Units="twips">' +
		'<PaperOrientation>Landscape</PaperOrientation>' +
		'<Id>Address</Id>' +
		'<PaperName>' + PAPER_NAME + '</PaperName>' +
		'<ObjectInfo>' +
		'<ImageObject>' +
		'<Name>' + IMAGE_OBJECT_NAME + '</Name>' +
		'<ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>' +
		'<BackColor Alpha="0" Red="255" Green="255" Blue="255"/>' +
		'<LinkedObjectName></LinkedObjectName>' +
		'<Rotation>Rotation0</Rotation>' +
		'<IsMirrored>False</IsMirrored>' +
		'<IsVariable>True</IsVariable>' +
		'<Image>' + PLACEHOLDER_IMAGE_BASE64 + '</Image>' +
		'<ScaleMode>Uniform</ScaleMode>' +
		'<BorderWidth>0</BorderWidth>' +
		'<BorderColor Alpha="255" Red="0" Green="0" Blue="0"/>' +
		'<HorizontalAlignment>Center</HorizontalAlignment>' +
		'<VerticalAlignment>Center</VerticalAlignment>' +
		'</ImageObject>' +
		'<Bounds X="' + LABEL_MARGIN_TWIPS + '" Y="' + LABEL_MARGIN_TWIPS + '" Width="' + width + '" Height="' + height + '"/>' +
		'</ObjectInfo>' +
		'</DieCutLabel>';
}

function multiplyTransform(m, ctm) {
	return [
		m[0] * ctm[0] + m[1] * ctm[2],
		m[0] * ctm[1] + m[1] * ctm[3],
		m[2] * ctm[0] + m[3] * ctm[2],
		m[2] * ctm[1] + m[3] * ctm[3],
		m[4] * ctm[0] + m[5] * ctm[2] + ctm[4],
		m[4] * ctm[1] + m[5] * ctm[3] + ctm[5],
	];
}

// An image is painted by mapping the unit square through the current transform,
// so its placed rectangle is the bounding box of that square's corners.
function unitSquareBounds(ctm) {
	let xs = [];
	let ys = [];
	[[0, 0], [1, 0], [0, 1], [1, 1]].forEach((corner) => {
		xs.push(ctm[0] * corner[0] + ctm[2] * corner[1] + ctm[4]);
		ys.push(ctm[1] * corner[0] + ctm[3] * corner[1] + ctm[5]);
	});
	let x = Math.min.apply(null, xs);
	let y = Math.min.apply(null, ys);
	return { x, y, width: Math.max.apply(null, xs) - x, height: Math.max.apply(null, ys) - y };
}

// The source PDF is a full page (letterhead, account details, a logo) with the
// QR code and the TotilPay logo as separate images on it. Walking the operator
// list's transform stack gives where every image is actually placed, without
// depending on coordinates that would break if the backend's layout changes.
function collectImagePlacements(operatorList) {
	let ctm = [1, 0, 0, 1, 0, 0];
	let stack = [];
	let placements = [];

	for (let i = 0; i < operatorList.fnArray.length; i++) {
		let fn = operatorList.fnArray[i];
		if (fn === pdfjsLib.OPS.save) {
			stack.push(ctm.slice());
		} else if (fn === pdfjsLib.OPS.restore) {
			ctm = stack.pop() || [1, 0, 0, 1, 0, 0];
		} else if (fn === pdfjsLib.OPS.transform) {
			ctm = multiplyTransform(operatorList.argsArray[i], ctm);
		} else if (fn === pdfjsLib.OPS.paintImageXObject || fn === pdfjsLib.OPS.paintJpegXObject || fn === pdfjsLib.OPS.paintImageMaskXObject) {
			placements.push(unitSquareBounds(ctm));
		}
	}

	return placements;
}

function isSquare(p) {
	let longest = Math.max(p.width, p.height);
	return longest > 0 && Math.abs(p.width - p.height) / longest < SQUARE_TOLERANCE;
}

// The QR is the largest square image on the page - this excludes the oblong
// logo without depending on its exact position.
function findQrImagePlacement(placements) {
	let square = placements.filter(isSquare);
	if (!square.length)
		return null;

	return square.reduce((a, b) => (a.width * a.height >= b.width * b.height ? a : b));
}

// The TotilPay logo is the largest oblong (non-square) image on the page.
function findLogoImagePlacement(placements) {
	let oblong = placements.filter((p) => !isSquare(p));
	if (!oblong.length)
		return null;

	return oblong.reduce((a, b) => (a.width * a.height >= b.width * b.height ? a : b));
}

// pdfUrl must be same-origin (a plain /benefit-api/... path), not the absolute
// URL prepareDownloadUrl() builds: in dev that one points at port 8080, which
// is a different origin than the dev server and sends no CORS headers, so the
// fetch would be blocked and the session cookie wouldn't be sent.
function renderPdfToPngBase64(pdfUrl) {
	return fetch(pdfUrl, { credentials: 'same-origin' })
		.then((response) => {
			if (!response.ok)
				throw new Error('could not fetch label PDF: HTTP ' + response.status);
			return response.arrayBuffer();
		})
		.then((pdfData) => pdfjsLib.getDocument({ data: pdfData }).promise)
		.then((pdf) => pdf.getPage(1))
		.then((page) => Promise.all([page.getOperatorList(), page.getTextContent()])
			.then(([operatorList, textContent]) => {
				let placements = collectImagePlacements(operatorList);
				return renderPage(page, findQrImagePlacement(placements), findLogoImagePlacement(placements), findLabelText(textContent));
			}))
		.then((canvas) => canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, ''));
}

// The program name, account ID and PAN are already on the PDF as page text, so
// the label reuses them instead of having the same values threaded through from
// each screen that prints. The PDF sets the PAN rotated and the other two
// upright, which is what tells them apart.
function findLabelText(textContent) {
	let upright = [];
	let rotated = [];

	textContent.items.forEach((item) => {
		if (!item.str || !item.str.trim())
			return;
		let line = { text: item.str.trim(), y: item.transform[5] };
		if (item.transform[1] === 0 && item.transform[2] === 0)
			upright.push(line);
		else
			rotated.push(line);
	});

	upright.sort((a, b) => b.y - a.y);
	let accountId = upright.find((line) => /account\s*id/i.test(line.text));
	let programName = upright.find((line) => line !== accountId);

	return {
		programName: programName ? programName.text : null,
		accountId: accountId ? accountId.text : null,
		pan: rotated.length ? rotated[0].text : null,
	};
}

function renderPage(page, qrPlacement, logoPlacement, labelText) {
	let unscaled = page.getViewport({ scale: 1 });
	// Scale so the QR lands at the size it prints at, not so the whole page
	// does - the page is mostly thrown away.
	let scale = qrPlacement
		? (QR_SIZE_PX / Math.max(qrPlacement.width, qrPlacement.height)) * QR_OVERSAMPLE
		: Math.min(TARGET_IMAGE_WIDTH_PX / unscaled.width, TARGET_IMAGE_HEIGHT_PX / unscaled.height);

	scale = Math.min(scale, MAX_PAGE_RENDER_PX / Math.max(unscaled.width, unscaled.height));

	let viewport = page.getViewport({ scale });
	let canvas = document.createElement('canvas');
	canvas.width = Math.ceil(viewport.width);
	canvas.height = Math.ceil(viewport.height);
	let context = canvas.getContext('2d');
	context.fillStyle = '#ffffff';
	context.fillRect(0, 0, canvas.width, canvas.height);

	return page.render({ canvasContext: context, viewport }).promise.then(() => {
		if (!qrPlacement)
			return canvas;

		let cropped = cropPlacement(canvas, viewport, qrPlacement);
		let logoCropped = logoPlacement ? cropPlacement(canvas, viewport, logoPlacement) : null;

		return rotateCanvas90(fitInk(composeLabel(cropped, logoCropped, labelText), COMPOSED_WIDTH_PX, COMPOSED_HEIGHT_PX));
	});
}

// Maps a PDF-space rectangle through the page viewport and copies that region
// of the already-rendered page into its own canvas.
function cropPlacement(canvas, viewport, placement) {
	let corner = viewport.convertToViewportPoint(placement.x, placement.y);
	let opposite = viewport.convertToViewportPoint(placement.x + placement.width, placement.y + placement.height);
	let x = Math.min(corner[0], opposite[0]);
	let y = Math.min(corner[1], opposite[1]);
	let width = Math.abs(opposite[0] - corner[0]);
	let height = Math.abs(opposite[1] - corner[1]);

	let cropped = document.createElement('canvas');
	cropped.width = Math.round(width);
	cropped.height = Math.round(height);
	let context = cropped.getContext('2d');
	context.fillStyle = '#ffffff';
	context.fillRect(0, 0, cropped.width, cropped.height);
	context.drawImage(canvas, x, y, width, height, 0, 0, cropped.width, cropped.height);
	return cropped;
}

// Rotates a canvas 90deg clockwise into a new one. Used to turn the portrait,
// PDF-proportioned composition into the label's actual landscape shape - see
// the comment above the layout constants for why.
function rotateCanvas90(source) {
	let output = document.createElement('canvas');
	output.width = source.height;
	output.height = source.width;
	let context = output.getContext('2d');
	context.translate(output.width, 0);
	context.rotate(Math.PI / 2);
	context.drawImage(source, 0, 0);
	return output;
}

// Lays the label out the way the PDF lays out the letter - see the ratios
// above - so staff see the same thing on both, just larger and rotated to
// use the label's long edge. Composed portrait onto a scratch canvas tall
// enough for the estimated block, so nothing is clipped before fitInk() can
// measure it; rotateCanvas90 turns the fitted result landscape afterwards.
function composeLabel(cropped, logoCropped, labelText) {
	let output = document.createElement('canvas');
	output.width = COMPOSED_WIDTH_PX;
	let context = output.getContext('2d');

	let messageWidth = QR_SIZE_PX * RULE_WIDTH_RATIO;
	let messageFontPx = Math.max(MIN_FONT_PX, Math.round(QR_SIZE_PX * MESSAGE_FONT_RATIO));
	context.font = messageFontPx + LABEL_FONT;
	let messageLines = wrapText(context, MESSAGE_TEXT, messageWidth);
	let messageBelowRatio = MESSAGE_GAP_RATIO + MESSAGE_LINE_RATIO * (messageLines.length - 1) + MESSAGE_FONT_RATIO * 0.25;

	let blockHeight = QR_SIZE_PX * (1 + BLOCK_ABOVE_RATIO + BLOCK_BELOW_RATIO + WEBSITE_BELOW_RATIO + messageBelowRatio);
	output.height = Math.max(COMPOSED_HEIGHT_PX, Math.ceil(blockHeight + LABEL_PADDING_PX * 2));
	context.fillStyle = '#ffffff';
	context.fillRect(0, 0, output.width, output.height);

	let topOffset = Math.max(LABEL_PADDING_PX, (output.height - blockHeight) / 2);

	let qrX = Math.round((output.width - QR_SIZE_PX) / 2);
	let qrY = Math.round(topOffset + QR_SIZE_PX * BLOCK_ABOVE_RATIO);
	let centerX = qrX + QR_SIZE_PX / 2;

	drawQrCode(context, cropped, qrX, qrY, QR_SIZE_PX);

	context.fillStyle = '#000000';
	context.textAlign = 'center';
	context.textBaseline = 'alphabetic';

	if (labelText.pan)
		drawFittedText(context, groupInFours(labelText.pan), centerX,
			qrY - QR_SIZE_PX * PAN_BASELINE_RATIO, QR_SIZE_PX, QR_SIZE_PX * PAN_FONT_RATIO);
	if (labelText.programName)
		drawFittedText(context, labelText.programName, centerX,
			qrY - QR_SIZE_PX * PROGRAM_BASELINE_RATIO, QR_SIZE_PX, QR_SIZE_PX * PROGRAM_FONT_RATIO);
	if (labelText.accountId)
		drawFittedText(context, labelText.accountId, centerX,
			qrY - QR_SIZE_PX * ACCOUNT_BASELINE_RATIO, QR_SIZE_PX, QR_SIZE_PX * ACCOUNT_FONT_RATIO);

	drawRule(context, centerX, qrY - QR_SIZE_PX * RULE_ABOVE_RATIO);
	let bottomRuleY = qrY + QR_SIZE_PX + QR_SIZE_PX * RULE_BELOW_RATIO;
	drawRule(context, centerX, bottomRuleY);

	let logoTop = bottomRuleY + QR_SIZE_PX * RULE_THICKNESS_RATIO / 2 + QR_SIZE_PX * LOGO_GAP_RATIO;
	if (logoCropped) {
		drawImageContain(context, logoCropped, centerX - QR_SIZE_PX * LOGO_WIDTH_RATIO / 2,
			logoTop, QR_SIZE_PX * LOGO_WIDTH_RATIO, QR_SIZE_PX * LOGO_HEIGHT_RATIO);
	}

	let logoBottom = logoTop + QR_SIZE_PX * LOGO_HEIGHT_RATIO;
	let websiteBaseline = logoBottom + QR_SIZE_PX * WEBSITE_BASELINE_RATIO;
	drawFittedText(context, WEBSITE_TEXT, centerX, websiteBaseline, QR_SIZE_PX, QR_SIZE_PX * WEBSITE_FONT_RATIO);

	context.textAlign = 'left';
	context.font = messageFontPx + LABEL_FONT;
	let messageX = centerX - messageWidth / 2;
	messageLines.forEach((line, i) => {
		context.fillText(line, messageX,
			websiteBaseline + QR_SIZE_PX * (MESSAGE_GAP_RATIO + MESSAGE_LINE_RATIO * i));
	});

	return output;
}

// Breaks text into lines at word boundaries so each fits maxWidth in the
// context's current font.
function wrapText(context, text, maxWidth) {
	let lines = [];
	let line = '';
	text.split(' ').forEach((word) => {
		let candidate = line ? line + ' ' + word : word;
		if (line && context.measureText(candidate).width > maxWidth) {
			lines.push(line);
			line = word;
		} else {
			line = candidate;
		}
	});
	if (line)
		lines.push(line);
	return lines;
}

// "6340030030450752758" -> "6340 0300 3045 0752 758": groups of four are far
// easier to read off a label than the PDF's single 9+10 split.
function groupInFours(pan) {
	return pan.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ');
}

// The layout places things by estimated extents - font ascents, and the logo's
// box, which includes whatever padding the logo image carries - so the block
// neither lands exactly centred nor takes up exactly the room it was sized
// for. Measure where the ink actually is and move it into the middle of the
// label, scaling it down only if the real ink wouldn't otherwise fit.
function fitInk(canvas, width, height) {
	let fitted = document.createElement('canvas');
	fitted.width = width;
	fitted.height = height;
	let context = fitted.getContext('2d');
	context.fillStyle = '#ffffff';
	context.fillRect(0, 0, width, height);

	let ink = findDarkBounds(canvas.getContext('2d'), canvas.width, canvas.height, INK_THRESHOLD);
	if (!ink)
		return fitted;

	let scale = Math.min(1,
		(width - INK_MARGIN_PX * 2) / ink.width,
		(height - INK_MARGIN_PX * 2) / ink.height);
	let drawWidth = ink.width * scale;
	let drawHeight = ink.height * scale;
	context.drawImage(canvas, ink.x, ink.y, ink.width, ink.height,
		// This canvas is still portrait, as the label is read, so its x axis
		// is the printed label's short edge and its left is the reader's left.
		Math.max(INK_MARGIN_PX, Math.round((width - drawWidth) / 2 - SHORT_EDGE_SHIFT_PX)),
		Math.round((height - drawHeight) / 2), drawWidth, drawHeight);
	return fitted;
}

// Scales an image to fit a box without distorting its aspect ratio, centered
// within it - used for the logo, whose cropped aspect ratio can vary slightly
// from the PDF's nominal one.
function drawImageContain(context, image, x, y, width, height) {
	let scale = Math.min(width / image.width, height / image.height);
	let drawWidth = image.width * scale;
	let drawHeight = image.height * scale;
	context.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
}

function drawRule(context, centerX, y) {
	let width = QR_SIZE_PX * RULE_WIDTH_RATIO;
	let thickness = QR_SIZE_PX * RULE_THICKNESS_RATIO;
	context.fillRect(centerX - width / 2, y - thickness / 2, width, thickness);
}

// Draws the QR re-cropped to its modules plus a QUIET_ZONE_RATIO margin.
function drawQrCode(context, cropped, x, y, size) {
	let code = findDarkBounds(cropped.getContext('2d'), cropped.width, cropped.height);
	if (!code) {
		context.drawImage(cropped, 0, 0, cropped.width, cropped.height, x, y, size, size);
		return;
	}

	// A QR is square, so square off the detected bounds before adding the margin.
	let codeSize = Math.max(code.width, code.height);
	let quietZone = codeSize * QUIET_ZONE_RATIO;
	let sourceSize = codeSize + quietZone * 2;
	let centerX = code.x + code.width / 2;
	let centerY = code.y + code.height / 2;

	context.drawImage(cropped,
		centerX - sourceSize / 2, centerY - sourceSize / 2, sourceSize, sourceSize,
		x, y, size, size);
}

// Sizes come from the PDF, but a longer program name than the sample's still
// has to stay on the label, so shrink anything that would overrun.
function drawFittedText(context, text, centerX, baselineY, maxWidth, fontSize) {
	let size = Math.max(MIN_FONT_PX, Math.round(fontSize));
	context.font = size + LABEL_FONT;
	while (size > MIN_FONT_PX && context.measureText(text).width > maxWidth) {
		size -= 1;
		context.font = size + LABEL_FONT;
	}
	context.fillText(text, centerX, baselineY, maxWidth);
}

function findDarkBounds(context, width, height, threshold = DARK_THRESHOLD) {
	let pixels = context.getImageData(0, 0, width, height).data;
	let minX = width;
	let minY = height;
	let maxX = -1;
	let maxY = -1;

	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			if (pixels[(y * width + x) * 4] < threshold) {
				if (x < minX) minX = x;
				if (x > maxX) maxX = x;
				if (y < minY) minY = y;
				if (y > maxY) maxY = y;
			}
		}
	}

	if (maxX < 0)
		return null;

	return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

// Attempts to print the given PDF (the same PDF the letter/browser-print flow
// uses) directly to a connected DYMO LabelWriter via the DYMO Connect SDK.
// pdfUrl must be a same-origin path - see renderPdfToPngBase64 above.
// Resolves true if the label was sent to the printer, or false if DYMO
// Connect / a LabelWriter isn't available - callers should fall back to the
// existing iframe + window.print() flow in that case.
export function printPanLabelViaDymo(pdfUrl) {
	return loadDymoFramework().then((available) => {
		if (!available) {
			console.warn(LOG_PREFIX, 'DYMO Connect framework did not load; using browser print.');
			return false;
		}

		let printerName = findLabelWriterPrinterName();
		if (!printerName) {
			console.warn(LOG_PREFIX, 'no connected DYMO LabelWriter found; using browser print.');
			return false;
		}

		return renderPdfToPngBase64(pdfUrl).then((base64Png) => {
			let label = window.dymo.label.framework.openLabelXml(buildLabelXml());
			label.setObjectText(IMAGE_OBJECT_NAME, base64Png);
			label.print(printerName);
			console.info(LOG_PREFIX, 'label sent to', printerName);
			return true;
		});
	}).catch((err) => {
		console.error(LOG_PREFIX, 'failed, using browser print instead.', err);
		return false;
	});
}
